import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { I18N, I18NContext, EBModal } from 'commons';
import { Formik, Form, Field } from 'formik';
import { Button } from 'reactstrap';
import FieldErrorMessage from 'components/forms/FieldErrorMessage';
import { orgId } from 'utils';
import * as yup from 'yup';
import { useSignUpAction } from './api';
import './sign-up-widget.scss';

export default function SignUpWidget({ eventSubscription }) {
  const i18n = React.useContext(I18NContext);
  const [isResultShow, setIsResultShow] = useState(false);
  const [isSignUpError, setIsSignUpError] = useState(false);
  const [resultContent, setResultContent] = useState('');
  const widgetRef = useRef();

  const [, handleSignUp] = useSignUpAction();

  useEffect(() => {
    widgetRef.current.resetForm();
  }, [eventSubscription]);

  return (
    <div className="sign-up-widget">
      <Formik
        innerRef={widgetRef}
        initialValues={{ fullName: '', mobile: '', email: '' }}
        validationSchema={() => {
          const requireField = eventSubscription.eventSubscriptionWebPage;
          const phoneExp = /^(((\+?1)?([\s\-]?[02-9])([\s\-]?\d){2})|(\(([\s\-]?[02-9])([\s\-]?\d){2}\)))([\s\-]?\d){7}$/;
          const fullNameSchema = requireField.requireName ?
            { fullName: yup.string().required('jquery.validation.field.required, eventSubscription.signUp.label.fullName') }
            :
            {};
          const mobileSchema = requireField.requireMobile ?
            {
              mobile: yup.string()
                .test('mobile', 'eventSubscription.validation.phone', (value) => {
                  if (value) {
                    return phoneExp.test(value);
                  }
                  return true;
                })
                .required('jquery.validation.field.required, eventSubscription.signUp.label.mobileNumber')
            }
            :
            {
              mobile: yup.string()
                .test('mobile', 'eventSubscription.validation.phone', (value) => {
                  if (value) {
                    return phoneExp.test(value);
                  }
                  return true;
                })
            };
          const emailSchema = requireField.requireEmail ?
            {
              email: yup.string()
                .email('eventSubscription.validation.email')
                .required('jquery.validation.field.required, new.registration.signup.create.account.label.emailAddress')
            } : {
              email: yup.string()
                .email('eventSubscription.validation.email')
            };
          return yup.object().shape({
            ...fullNameSchema,
            ...mobileSchema,
            ...emailSchema

          });
        }

        }
        onSubmit={(values) => {
          setIsSignUpError(false);
          handleSignUp({
            orgId,
            eventSubscriptionSignUpStr: JSON.stringify({
              ...values,
              widgets: eventSubscription.widgets
            })
          }, (resData) => {
            widgetRef.current.resetForm();
            if (resData && (resData.statusCode == 200 || resData.statusCode == '200 OK')) {
              setIsResultShow(true);
            } else {
              setIsSignUpError(true);
            }
            setResultContent(resData.result);
          });
        }}
      >
        {({
          values,
          errors,
          touched,
          handleSubmit }) => (
          <Form>
            <div className="sign-up-widget-top gray-bg no-radius">
              <div><small id="labeltitle"><I18N>eventSubscription.signUp.title</I18N></small></div>
              <div className="group-name" eb-test="sign_up_widget_title_group_name">
                {eventSubscription.groupName}
              </div>
              <p><I18N>form.fields.are.required</I18N></p>
              <div className="form-group row mx-0">
                <label htmlFor="fullName" id="fullName-Label">
                  <I18N>eventSubscription.signUp.label.fullName</I18N>
                  {
                    eventSubscription.eventSubscriptionWebPage.requireName &&
                    <span aria-hidden="true" className="required-star">*</span>
                  }
                  {
                    eventSubscription.eventSubscriptionWebPage.requireName &&
                    <span className="sr-only"><I18N>information.label.required</I18N></span>
                  }
                </label>
                <Field
                  autoComplete="off"
                  id="fullName"
                  name="fullName"
                  className="form-control"
                  required={eventSubscription.eventSubscriptionWebPage.requireName}
                  aria-invalid={touched.fullName && !!errors.fullName}
                  aria-describedby={touched.fullName && errors.fullName ? 'fullNameInvalidMsg' : null}
                  aria-label={i18n['eventSubscription.signUp.label.fullName']}
                  eb-test="sign_up_widget_form_field_fullname"
                />
                {
                  touched.fullName && errors.fullName &&
                  <FieldErrorMessage id="fullNameInvalidMsg" aria-live="assertive">
                    <I18N>{errors.fullName}</I18N>
                  </FieldErrorMessage>
                }
              </div>
              <div className="form-group row mx-0">
                <label htmlFor="mobile" id="mobile-Label">
                  <I18N>eventSubscription.signUp.label.mobileNumber</I18N>
                  {
                    eventSubscription.eventSubscriptionWebPage.requireMobile &&
                    <span aria-hidden="true" className="required-star">*</span>
                  }
                  {
                    eventSubscription.eventSubscriptionWebPage.requireMobile &&
                    <span className="sr-only"><I18N>information.label.required</I18N></span>
                  }
                </label>
                <Field
                  autoComplete="off"
                  id="mobile"
                  name="mobile"
                  className="form-control"
                  required={eventSubscription.eventSubscriptionWebPage.requireMobile}
                  aria-invalid={touched.mobile && !!errors.mobile}
                  aria-describedby={touched.mobile && errors.mobile ? 'phoneInvalidMsg' : null}
                  aria-label={i18n['eventSubscription.signUp.label.mobileNumber']}
                  eb-test="sign_up_widget_form_field_mobile"
                />
                {
                  touched.mobile && errors.mobile &&
                  <FieldErrorMessage id="phoneInvalidMsg" aria-live="assertive">
                    <I18N>{errors.mobile}</I18N>
                  </FieldErrorMessage>
                }
              </div>
              <div className="form-group row mx-0">
                <label htmlFor="email" id="email-Label">
                  <I18N>new.registration.signup.create.account.label.emailAddress</I18N>
                  {
                    eventSubscription.eventSubscriptionWebPage.requireEmail &&
                    <span aria-hidden="true" className="required-star">*</span>
                  }
                  {
                    eventSubscription.eventSubscriptionWebPage.requireEmail &&
                    <span className="sr-only"><I18N>information.label.required</I18N></span>
                  }
                </label>
                <Field
                  autoComplete="off"
                  id="email"
                  name="email"
                  className="form-control"
                  required={eventSubscription.eventSubscriptionWebPage.requireEmail}
                  aria-invalid={touched.email && !!errors.email}
                  aria-describedby={touched.email && errors.email ? 'emailInvalidMsg' : null}
                  aria-label={i18n['new.registration.signup.create.account.label.emailAddress']}
                  eb-test="sign_up_widget_form_field_email"
                />
                {
                  touched.email && errors.email &&
                  <FieldErrorMessage id="emailInvalidMsg" aria-live="assertive">
                    <I18N>{errors.email}</I18N>
                  </FieldErrorMessage>
                }
              </div>
              <Field type="hidden" id="widgetId" name="widgetId" value={values.widgetId} />
            </div>
            <div className="form-group row mx-0 sign-up-widget-action">
              <Button
                type="submit"
                color="success"
                size="lg"
                block
                eb-test="sign_up_widget_button_submit"
                onClick={handleSubmit}
              >
                <I18N>eventSubscription.signUp.btn.submit</I18N>
              </Button>
            </div>
            {isSignUpError &&
              <div className="margin5-T text-center sign-up-widget-alert">
                <label className="control-label">{resultContent}</label>
              </div>
            }
            <div className="sign-up-widget-bottom">
              {eventSubscription.keyword &&
                <div className="keyword">
                  <div className="float-box"><I18N>eventSubscription.signUp.message.or</I18N></div>
                  <hr />
                  <div className="text-center h3-txt">
                    <small><I18N>eventSubscription.signUp.message.text</I18N></small>
                    {eventSubscription.keyword.keyword}
                    <small><I18N>eventSubscription.signUp.message.to</I18N></small>
                    {eventSubscription.nixleAgency.shortCode}
                  </div>
                </div>
              }
              <div className="margin10-T">
                <small>
                  <I18N>eventSubscription.signUp.message.apply</I18N>
                  <a
                    rel="noopener noreferrer"
                    href="http://www.nixle.com/resident-terms-service/"
                    target="_blank"
                    eb-test="sign_up_widget_link_terms"
                  >
                    <I18N>eventSubscription.signUp.message.terms</I18N>
                  </a>
                  <I18N>eventSubscription.signUp.message.and</I18N>
                  <a
                    rel="noopener noreferrer"
                    href="https://www.everbridge.com/company/legal/privacy-policy/"
                    target="_blank"
                    eb-test="sign_up_widget_link_privacy"
                  >
                    <I18N>eventSubscription.signUp.message.privacy</I18N>.
                  </a>
                </small>
              </div>
            </div>
          </Form>
        )}
      </Formik>
      <EBModal
        isOpen={isResultShow}
        type="warn"
        toggleModal={() => { setIsResultShow(false); }}
        title={i18n['eventSubscription.signUp.message.title.succeed']}
      >
        <div>{resultContent}</div>
      </EBModal>
    </div>
  );
}

SignUpWidget.propTypes = {
  eventSubscription: PropTypes.shape().isRequired
};
